import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ItemHandlingList from '../../../components/dashboard/items/item-handling-list';
import { fetchItemList as fetchItemListApi, handleItem as handleItemApi } from '../../../api/inventory/itemLists';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const ItemHandlingPage = () => {
  const { item_list_id } = useParams();
  const navigate = useNavigate();
  const [itemList, setItemList] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchItemList();
  }, [item_list_id]);

  const fetchItemList = async () => {
    try {
      setIsLoading(true);
      const result = await fetchItemListApi(item_list_id);
      setItemList(result.data);
    } catch (err) {
      setError(`Det oppstod en feil ved lasting av data: ${err.message}`);
      console.error('Error fetching item list:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemHandled = (itemListItemId, updatedData) => {
    if (itemListItemId === 'REFETCH_NEEDED') {
      // Refetch the entire list when remaining quantity was created
      fetchItemList();
      return;
    }

    // Update the local state with the handled item
    setItemList(prevList => {
      if (!prevList) return prevList;

      return {
        ...prevList,
        item_list_items: prevList.item_list_items.map(item => {
          if (item.id === itemListItemId) {
            return {
              ...item,
              order_item: {
                ...item.order_item,
                ...updatedData.order_item
              }
            };
          }
          return item;
        })
      };
    });
  };

  const getCompletionStatus = () => {
    if (!itemList?.item_list_items) return { completed: 0, total: 0 };

    const total = itemList.item_list_items.length;
    const completed = itemList.item_list_items.filter(item => {
      const orderItem = item.order_item || item;
      return orderItem.returned_to_storage || orderItem.left_in_vehicle;
    }).length;

    return { completed, total };
  };

  const { completed, total } = getCompletionStatus();
  const isFullyHandled = completed === total && total > 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Laster varehåndteringsliste...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
              <button
                onClick={() => navigate(-1)}
                className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Gå tilbake
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Tilbake
          </button>

          <div className="bg-white shadow-sm rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {itemList?.title || 'Varehåndteringsliste'}
                </h1>
                <div className="flex items-center space-x-4 mt-1">
                  <p className="text-gray-600">
                    Håndter retur av varer til lager eller flytting til kjøretøy
                  </p>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    itemList?.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                    itemList?.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                    itemList?.status === 'approved' ? 'bg-green-100 text-green-800' :
                    itemList?.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {itemList?.status === 'draft' ? 'Utkast' :
                     itemList?.status === 'pending_approval' ? 'Venter på godkjenning' :
                     itemList?.status === 'approved' ? 'Godkjent' :
                     itemList?.status === 'rejected' ? 'Avvist' :
                     itemList?.status || 'Ukjent'}
                  </span>
                </div>
              </div>

              {isFullyHandled && (
                <div className="flex items-center text-green-600">
                  <CheckCircleIcon className="h-6 w-6 mr-2" />
                  <span className="font-medium">Alle varer håndtert</span>
                </div>
              )}
            </div>

            {/* Progress indicator */}
            <div className="mt-6">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>Fremgang</span>
                <span>{completed} av {total} varer håndtert</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Item Handling List */}
        <div className="bg-white shadow-sm rounded-lg">
          <ItemHandlingList
            itemList={itemList}
            isLoading={false}
            onItemHandled={handleItemHandled}
            showBanner={false}
          />
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">
            Håndteringsinstruksjoner
          </h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Returner til lager:</strong> Flytt varen tilbake til opprinnelig lagerlokasjon</li>
            <li>• <strong>Flytt til kjøretøy:</strong> Behold varen i kjøretøyet for neste oppdrag</li>
            <li>• Alle varer må håndteres før oppgaven kan fullføres</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ItemHandlingPage;
